import type { Copy } from '../types/copy';

export const copyEn: Copy = {
  seo: {
    title: 'Alma Yoga | Private Luxury Yoga in New York City',
    description:
      'Private, personalized yoga experience in NYC. In-home, online, and hotel sessions with a certified instructor. Recovery, mobility, spirituality. Bilingual.',
  },
  nav: {
    services: 'Services',
    philosophy: 'Philosophy',
    about: 'About',
    faq: 'FAQ',
    contact: 'Contact',
    book: 'Book a Session',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    skip: 'Skip to content',
    language: 'Language',
    call: 'Call Alma Yoga',
  },
  hero: {
    tagline: 'Yoga Beyond the Mat',
    h1: 'Private luxury yoga practice, designed around you.',
    sub: 'In your space. For your goals. For your life.',
    description:
      'Alma Yoga offers intimate one-on-one sessions shaped around your body, energy and intentions. In-home across New York City. Online, anywhere in the world.',
    cta: 'Schedule a Private Session',
    secondary: 'Explore offerings',
    imageAlt:
      'Private yoga instructor holding warrior pose on a Manhattan rooftop at golden hour with the Empire State Building behind',
  },
  concept: {
    title: 'What is Alma Yoga?',
    lead: 'A unique, personalized practice. Designed for one person — you.',
    paragraphs: [
      'Alma Yoga is a private, personalized yoga practice designed around your needs, your body, your energy and your intentions.',
      'Every session starts from where you are today — physically, mentally, spiritually. The practice adapts to you.',
      'With 10 years of personal practice and a 200-hour Yoga Alliance certification, I build sessions that go beyond asana: breathwork, philosophy, and how these principles actually hold up in your life.',
      'Recovering from travel, training for a race, working around an injury, or looking for something deeper — your session is designed for exactly that.',
    ],
    facts: [
      { value: '10', label: 'years of practice' },
      { value: '2016', label: 'teaching since' },
      { value: 'EN / ES', label: 'sessions in both languages' },
    ],
    imageAlt:
      'Yoga mat, blanket and block in a sunlit Manhattan loft prepared for a private in-home yoga session',
  },
  experience: {
    title: 'A Private Yoga Experience Beyond the Mat',
    subtitle: 'What to expect',
    intro: 'Around 60 minutes. Eight movements, one continuous practice.',
    steps: [
      {
        title: 'Connection',
        body: 'We start with a conversation — your body, your energy, your intentions, and anything physical I should know.',
      },
      {
        title: 'Personalization',
        body: 'I shape the session in real time, adjusting every element as we go.',
      },
      {
        title: 'Movement',
        body: 'Asana crafted for your body: strength, flexibility, mobility or recovery.',
      },
      {
        title: 'Breath & Awareness',
        body: 'Pranayama and mindful transitions to settle the nervous system and sharpen attention.',
      },
      {
        title: 'Philosophy',
        body: 'Yoga principles applied to real life — breathing through difficulty, finding stability, learning to let go.',
      },
      {
        title: 'Meditation',
        body: 'Guided or silent. Whatever you need that day.',
      },
      {
        title: 'Savasana',
        body: 'An extended final rest, where the practice settles in and you recover deeply.',
      },
      {
        title: 'Integration',
        body: 'A practice that stays with you long after you leave the mat.',
      },
    ],
    emphasis: 'Every element has a purpose. Every moment is designed for you.',
    cta: 'Book Your First Session',
  },
  services: {
    title: 'Our Offerings',
    intro:
      'A unique, personalized practice. One-on-one, with your partner, or with a small group of friends or family.',
    learnMore: 'Learn more',
    book: 'Book this session',
    close: 'Close',
    includes: "What's included",
    idealForLabel: 'Ideal for',
    items: [
      {
        id: 'in-home',
        label: 'In-home',
        title: 'Private In-Home Sessions',
        description:
          'Your practice, in the privacy of your own home. On your schedule, in your space, with focused attention on you.',
        features: [
          'One-on-one personalization',
          'Mornings, evenings and weekends',
          'Tailored to your space and energy',
          'Complete privacy and comfort',
        ],
        idealFor:
          'Professionals with full calendars, anyone who values privacy, and people who practice best in their own space.',
        alt: 'Private in-home yoga instructor guiding a client in a luxury New York City apartment',
      },
      {
        id: 'online',
        label: 'Online',
        title: 'Online Private Sessions',
        description:
          'Alma Yoga wherever you are. The same personalization over video — made for travelers, busy professionals and practitioners outside New York.',
        features: [
          'Full personalization via video',
          'Available worldwide',
          'Same quality, online',
          'Flexible scheduling across time zones',
        ],
        idealFor:
          'Frequent travelers, clients outside New York, and anyone who wants consistency wherever they are.',
        alt: 'Online private yoga session on a laptop in a calm home setting',
      },
      {
        id: 'athletes',
        label: 'Athletes',
        title: 'Recovery & Performance Yoga',
        description:
          'A practice that complements your training. Mobility, flexibility, recovery and body awareness — for runners, boxers, lifters and anyone who trains hard.',
        features: [
          'Mobility and flexibility work',
          'Recovery and regeneration',
          'Body awareness and balance',
          'Complements any sport',
        ],
        idealFor:
          'Runners, boxers, strength athletes and gym regulars looking for mobility, recovery and injury prevention.',
        alt: 'Athlete practicing recovery yoga for mobility in a Brooklyn training space',
      },
      {
        id: 'travelers',
        label: 'Travelers',
        title: 'Travel & Hotel Sessions',
        description:
          'Keep your practice while you travel. Recovery from long flights, jet lag relief and grounding sessions in your hotel suite or anywhere in the city.',
        features: [
          'In-hotel sessions across NYC',
          'Jet lag and travel recovery',
          'Timing that fits your itinerary',
          'Personalized to how you feel on arrival',
        ],
        idealFor:
          'Guests staying in New York hotels, business travelers and anyone recovering from a long-haul flight.',
        alt: 'Restorative private yoga session in a luxury NYC hotel suite overlooking the Manhattan skyline',
      },
      {
        id: 'astrology',
        label: 'Birth chart',
        title: 'Birth Chart Yoga Sessions',
        description:
          'What if your practice was designed around your birth chart? We use your natal chart as a tool for self-exploration — a way to find balance and cultivate specific qualities.',
        features: [
          'Birth chart as an exploration tool',
          'Energy-aligned practice',
          'Self-discovery through yoga',
          'Symbolic, never predictive',
        ],
        idealFor:
          'People curious about self-exploration who want a practice with a symbolic, reflective layer.',
        alt: 'Astrology-inspired yoga practice with a hand-drawn birth chart and meditation mudra',
      },
      {
        id: 'groups',
        label: 'Groups',
        title: 'Intimate Group Sessions',
        description:
          "Share the practice with your partner, family or closest friends. Designed around the group's energy.",
        features: [
          'Couples and families',
          'Close groups of friends',
          'Personalized for the collective',
          'Private, intimate setting',
        ],
        idealFor: 'Couples, families and close friends who want to share a private practice.',
        alt: 'Small private group yoga session for friends in a sunlit SoHo loft',
      },
    ],
    styles: {
      title: 'Yoga Styles',
      items: ['Hatha', 'Vinyasa Flow', 'Yin', 'Restorative', 'Slow Flow', 'Gentle Yoga'],
      more: 'and more',
    },
  },
  instructor: {
    title: 'Your Instructor',
    name: 'Pia',
    role: 'Founder & Owner',
    motto: 'Adventurer of the invisible, warrior of the spirit.',
    bio: 'To me, yoga is a practice of bringing body, mind, and emotions into harmony. A space to reconnect, find calm and joy, and come back home to ourselves — honoring the body as our only home.',
    closing: 'Cultivating presence and patience.',
    credentialsTitle: 'Training & background',
    credentials: [
      'Certified Yoga Instructor — 200-hour, Yoga Alliance',
      'Trained at Sampoorna Yoga, India',
      '10 years of personal practice',
      'Teaching since 2016',
      'Degree in Public Relations',
    ],
    imageAlt: 'Pia, certified private yoga instructor in New York City',
    cta: 'Book a session with Pia',
  },
  why: {
    title: 'Why Choose Alma Yoga?',
    items: [
      {
        title: 'Private & personalized',
        body: 'Designed entirely around you — everything personalized for your needs.',
      },
      {
        title: 'Flexible & convenient',
        body: 'In your home, online, or in your hotel. Weekends and holidays included.',
      },
      {
        title: 'Expert & certified',
        body: '10 years of dedicated practice and professional training.',
      },
      {
        title: 'Holistic',
        body: 'Beyond asana: breath, philosophy and how it applies to your life.',
      },
      {
        title: 'Bilingual',
        body: 'Sessions in English or Spanish — whichever feels like home.',
      },
      {
        title: 'Wellness & recovery',
        body: 'For athletes, travelers and bodies that need care.',
      },
      {
        title: 'Spiritual & modern',
        body: 'An ancient practice, in a contemporary New York context.',
      },
      {
        title: 'Your pace',
        body: 'Your rhythm. Your journey. Just you and the practice.',
      },
    ],
  },
  philosophy: {
    title: 'Yoga Beyond the Mat',
    opening: 'The practice begins on the mat. The real work happens in life.',
    learnLabel: 'Through yoga, we learn',
    lessons: [
      'How to breathe when things get difficult',
      'How to observe without reacting right away',
      'How to find stability in discomfort',
      'How to let go of what no longer serves',
      'How to show up with intention',
    ],
    closing:
      "These are practices, not destinations. They apply on the mat and in every moment after it. That's where the real change lives.",
  },
  testimonials: {
    title: 'What Our Clients Say',
    items: [
      {
        quote:
          "Pia's approach is completely different from any yoga class I've taken. It's not about following along. It's about being met where I am.",
        name: 'Daniela R.',
        context: 'Architect · Upper West Side',
      },
      {
        quote:
          'As an athlete, I needed recovery that actually understood what my body needed. This practice changed how I train.',
        name: 'Marcus T.',
        context: 'Marathon runner & amateur boxer',
      },
      {
        quote:
          'The personalization is remarkable. She listens, adapts, and builds something that feels made just for me.',
        name: 'Sofía L.',
        context: 'Frequent traveler · Madrid & New York',
      },
      {
        quote:
          'For the first time, yoga felt spiritual and practical. I use what I learned every day.',
        name: 'James K.',
        context: 'Attorney · Tribeca',
      },
    ],
  },
  memberships: {
    title: 'Alma Memberships',
    intro:
      'Two personalized journeys. One intention: to help you reconnect with yourself, honor your own rhythms and cultivate greater awareness, on and off the mat.',
    byline: 'Alma Yoga · NYC',
    getStarted: 'Get Started',
    footer: 'For pricing and more information, please get in touch.',
    contactCta: 'Contact Us',
    tiers: [
      {
        id: 'essential',
        name: 'Alma Essential',
        term: 'Monthly Membership',
        description:
          'A personalized monthly journey to reconnect with yourself through movement, self-awareness and mindful living.',
        groups: [
          {
            title: 'Your Personalized Practice',
            items: [
              { text: '4 private yoga sessions per month' },
              { text: 'A personalized yoga practice tailored to your needs and goals' },
              { text: 'Text support the day after each session' },
              { text: 'One 15-minute private monthly check-in' },
            ],
          },
          {
            title: 'Your Personalized Wellness Toolkit',
            items: [
              {
                label: 'Morning Ritual:',
                text: 'A short morning practice with an illustrated asana guide to help you begin your day with intention.',
              },
              {
                label: 'Sleep & Emotional Balance:',
                text: 'A written asana guide to support relaxation, emotional balance and a more mindful wind-down.',
              },
            ],
          },
        ],
      },
      {
        id: 'signature',
        name: 'Alma Signature',
        term: '3-Month Membership',
        description:
          'A deeper journey into your personal rhythms, emotional wellbeing and the practices that support your growth, thoughtfully tailored to you over three months.',
        groups: [
          {
            title: 'Your Personalized Practice',
            items: [
              { text: '12 private yoga sessions over 3 months' },
              { text: 'A personalized yoga practice tailored to your needs and goals' },
              { text: 'Text support the day after each session' },
              { text: 'One 15-minute private monthly check-in' },
            ],
          },
          {
            title: 'Your Personalized Wellness Toolkit',
            items: [
              {
                label: 'Anxiety & Emotional Balance:',
                text: 'A personalized guided audio practice to help you navigate moments of anxiety and emotional overwhelm.',
              },
              {
                label: 'Stress & Grounding:',
                text: 'An illustrated asana guide to release tension, reconnect with your body and cultivate a sense of grounding.',
              },
              {
                label: 'Sleep & Wind Down:',
                text: 'A personalized illustrated asana guide designed to help you slow down, unwind and prepare for rest.',
              },
            ],
          },
          {
            title: 'Integrative Wellbeing Support',
            items: [
              {
                text: 'Access to a kinesiologist when needed, offering additional support for movement, mobility and physical wellbeing.',
              },
            ],
          },
        ],
      },
    ],
  },
  press: {
    featuredIn: 'As featured in',
    byline: 'By Pía Escobedo',
    headline: 'Presence Is a State of Happiness',
    sub: "Pia Escobedo's wellness philosophy, featured in Beyond the Lobby.",
    excerpt:
      'The practice of yoga offers a way back to the present through the breath. After all, our only real life exists in the present moment: the past is gone, and the future is only imagination.',
    cta: 'Read full article',
    readMore: 'Read more',
    close: 'Close article',
    prev: 'Previous page',
    next: 'Next page',
    page: 'Page',
    of: 'of',
    pageAlts: [
      'Beyond the Lobby article "Presence Is a State of Happiness" by Pía Escobedo, page one',
      'Daily wellness practices by Pía Escobedo in Beyond the Lobby, illustrated page',
      'Pía Escobedo, Argentine yoga teacher in New York, practicing yoga on the beach',
      'Beyond the Lobby page on private in-room hotel yoga sessions with Pía Escobedo',
    ],
    cards: [
      {
        title: 'Daily Wellness Rituals',
        body: 'Five small practices Pía returns to every day — from the first glass of water to a gratitude list before sleep.',
        page: 1,
      },
      {
        title: 'Meet Pía Escobedo',
        body: 'An Argentine yoga and meditation teacher bringing presence and balance to fast-paced city life.',
        page: 2,
      },
      {
        title: 'Yoga in Your Hotel Room',
        body: 'Private in-room sessions in English and Spanish — wellness woven into a New York stay.',
        page: 3,
      },
    ],
  },
  approach: {
    title: 'What Makes a Practice Personalized?',
    intro: 'Your practice can be tailored to five things — usually all at once.',
    items: [
      { title: 'Your body', body: 'Physical level, flexibility, strength, injuries or limitations.' },
      { title: 'Your energy', body: 'Whether you need grounding, lifting or balancing.' },
      {
        title: 'Your goals',
        body: 'Performance, recovery, spiritual exploration or stress relief.',
      },
      {
        title: 'Your life moment',
        body: "What you're navigating right now — transitions, challenges, growth.",
      },
      {
        title: 'Your interests',
        body: 'Philosophy, breathwork, meditation, movement, or a blend of all of them.',
      },
    ],
    kinesiology: {
      title: 'Working with an injury?',
      body: 'If you have specific physical concerns, we can bring in a kinesiologist consultation so every session is safe and aligned with any existing condition.',
    },
  },
  faq: {
    title: 'Questions?',
    intro: 'What people usually ask before a first session. Anything else — just write.',
    contactLink: 'Ask a question',
    items: [
      {
        q: "What if I'm a beginner?",
        a: "Alma Yoga is designed for all levels. If you're new to yoga, we'll build a foundation tailored to your body and pace — with patience, care and space to grow.",
      },
      {
        q: 'What if I have injuries or limitations?',
        a: "This is where personalization matters most. We work within your body's needs and can consult a kinesiologist if necessary. Your practice adapts to you.",
      },
      {
        q: 'How long are sessions?',
        a: 'Sessions are around 60 minutes, and duration can adapt to your needs and schedule.',
      },
      {
        q: "Can I do sessions if I'm traveling?",
        a: "Absolutely. Online sessions work anywhere, and if you're in New York we can arrange sessions in your hotel.",
      },
      {
        q: 'How does astrology-inspired yoga work?',
        a: 'We use your birth chart as a symbolic tool for self-exploration. It helps identify qualities to cultivate and areas looking for balance, and your practice is designed around those insights.',
      },
      {
        q: 'Is this spiritual or fitness-based?',
        a: "Both, and neither exclusively. We weave together movement, breath, philosophy and mindfulness. You'll feel the physical benefits and the depth.",
      },
      {
        q: 'How do I book my first session?',
        a: "Use the booking form or call directly. We'll have a short conversation about your needs, goals and preferences to design your first practice.",
      },
      {
        q: 'Can I book sessions with friends or family?',
        a: 'Yes. Small group sessions are tailored to couples, families or close friends. Same personalization, shared experience.',
      },
    ],
  },
  contact: {
    title: 'Ready to Begin?',
    sub: "Let's create a practice designed for you.",
    form: {
      name: 'Full name',
      email: 'Email',
      phone: 'Phone',
      service: "Service you're interested in",
      servicePlaceholder: 'Select a service',
      other: 'Other / not sure yet',
      schedule: 'Preferred schedule',
      scheduleOptions: {
        morning: 'Morning',
        afternoon: 'Afternoon',
        evening: 'Evening',
        flexible: 'Flexible',
      },
      language: 'Session language',
      languageOptions: { en: 'English', es: 'Spanish' },
      message: 'Message',
      optional: 'Optional',
      messagePlaceholder: 'Tell me about your practice, goals or needs',
      required: 'Required',
      submit: 'Send via WhatsApp',
      whatsappIntro: "Hi Pia, I'd like to request a session with Alma Yoga.",
      errors: {
        name: 'Please enter your name.',
        email: 'Please enter a valid email address.',
        phone: 'Please enter a phone number where I can reach you.',
        service: 'Please choose a service.',
      },
      successTitle: 'Almost there — just hit send.',
      successBody:
        "WhatsApp has opened with your details ready. Send the message and I'll reply within 24 hours. If it didn't open, message me at +1 (917) 805-4352.",
      another: 'Send another request',
      privacyNote: 'Your details are only used to arrange your session.',
    },
    info: {
      phone: 'Phone',
      email: 'Email',
      whatsapp: 'WhatsApp',
      whatsappValue: 'Message in English or Spanish',
      location: 'Where',
      locationValue: 'New York City — in-home, online and hotels',
      availability: 'Availability',
      availabilityValue: 'Flexible, including weekends and holidays',
      response: "I'll be in touch within 24 hours.",
    },
  },
  footer: {
    tagline: 'Yoga Beyond the Mat',
    explore: 'Explore',
    contact: 'Contact',
    language: 'Language',
    follow: 'Follow',
    rights: 'All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms',
    location: 'New York City',
  },
};
